import { find, head, is, map, path, prop, propEq, toUpper } from "ramda";
import localForage from "localforage";
import { PaikallisenTietovarastonAvain } from "@/enums";

/**
 Tässä tiedostossa määritellään, mitä asetuksia eli rajoitekriteerejä kullekin
 rajoitteen kohteelle on valittavissa.
 */
export const getAsetuksenKohdekomponentti = async (
  asetuksenKohdeavain,
  kohdevaihtoehdot = [],
  koulutustyyppi,
  isReadOnly = false,
  locale,
  index,
  inputId
) => {
  const localeUpper = toUpper(locale);
  const kujalisamaareet = await localForage.getItem(
    PaikallisenTietovarastonAvain.JOISTA_LISAKSI
  );

  const ajalla = head(
    await localForage.getItem(PaikallisenTietovarastonAvain.AJALLA)
  );

  const maaraaikaOption = {
    value: `${path(["koodisto", "koodistoUri"], ajalla)}_${ajalla.koodiarvo}`,
    label: ajalla.metadata[localeUpper].nimi
  };

  function getOptionObjects(optionKeys) {
    return map(keyOrObject => {
      if (is(String, keyOrObject)) {
        return find(propEq("value", keyOrObject), kohdevaihtoehdot);
      } else {
        return keyOrObject;
      }
    }, optionKeys).filter(Boolean);
  }

  let properties = {
    erityisetKoulutustehtavat: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects(
        [
          koulutustyyppi === "1" ? "opetustehtavat" : null,
          "toimintaalue",
          "opetuskielet",
          koulutustyyppi === "1" ? "opetuksenJarjestamismuodot" : null,
          maaraaikaOption,
          "oppilaitokset"
        ].filter(Boolean)
      ),
      value: ""
    },
    luvanTaiteenalat: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects([
        maaraaikaOption,
        "toimintaalue",
        "muutKunnatJoistaOppilaita",
        "opetuskielet",
        "muutEhdot"
      ]),
      value: ""
    },
    valtakunnallisetKehittamistehtavat: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects([maaraaikaOption].filter(Boolean)),
      value: ""
    },
    erityisenKoulutustehtavanRahoitus: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects([maaraaikaOption].filter(Boolean)),
      value: ""
    },
    lukumaara: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: map(maare => {
        const koodistoUri = path(["koodisto", "koodistoUri"], maare);
        return {
          value: `${koodistoUri}_${maare.koodiarvo}`,
          label: maare.metadata[localeUpper].nimi
        };
      }, kujalisamaareet)
    },
    muutEhdot: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects(
        [
          maaraaikaOption,
          koulutustyyppi === "1" ? "opetustehtavat" : null,
          koulutustyyppi === "4" ? "luvanTaiteenalat" : null,
          "toimintaalue",
          koulutustyyppi === "4" ? "muutKunnatJoistaOppilaita" : null,
          "opetuskielet",
          koulutustyyppi === "1" ? "opetuksenJarjestamismuodot" : null,
          koulutustyyppi === "1" || koulutustyyppi === "2"
            ? "erityisetKoulutustehtavat"
            : null,
          koulutustyyppi === "1" || koulutustyyppi === "2"
            ? "oppilaitokset"
            : null
        ].filter(Boolean)
      ),
      value: ""
    },
    oikeusSisaoppilaitosmuotoiseenKoulutukseen: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects(
        [
          "toimintaalue",
          "opetuskielet",
          maaraaikaOption,
          "oppilaitokset"
        ].filter(Boolean)
      ),
      value: ""
    },
    opetuksenJarjestamismuodot: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects(
        [
          koulutustyyppi === "1" ? "opetustehtavat" : null,
          "toimintaalue",
          "opetuskielet",
          maaraaikaOption,
          "oppilaitokset"
        ].filter(Boolean)
      ),
      value: ""
    },
    opetuskielet: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects(
        [
          maaraaikaOption,
          koulutustyyppi === "1" ? "opetustehtavat" : null,
          koulutustyyppi === "4" ? "luvanTaiteenalat" : null,
          "toimintaalue",
          koulutustyyppi === "4" ? "muutKunnatJoistaOppilaita" : null,
          koulutustyyppi === "4" ? "muutEhdot" : null,
          koulutustyyppi === "1" || koulutustyyppi === "2"
            ? "oppilaitokset"
            : null
        ].filter(Boolean)
      ),
      value: ""
    },
    opetustehtavat: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects([maaraaikaOption, "oppilaitokset"]),
      value: ""
    },
    toimintaalue: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects(
        [
          maaraaikaOption,
          koulutustyyppi === "1" ? "opetustehtavat" : null,
          koulutustyyppi === "4" ? "luvanTaiteenalat" : null,
          koulutustyyppi === "4" ? "opetuskielet" : null,
          koulutustyyppi === "4" ? "muutEhdot" : null,
          koulutustyyppi === "1" || koulutustyyppi === "2"
            ? "oppilaitokset"
            : null
        ].filter(Boolean)
      ),
      value: ""
    },
    muutKunnatJoistaOppilaita: {
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options: getOptionObjects(
        [
          maaraaikaOption,
          koulutustyyppi === "4" ? "luvanTaiteenalat" : null,
          koulutustyyppi === "4" ? "opetuskielet" : null,
          koulutustyyppi === "4" ? "muutEhdot" : null
        ].filter(Boolean)
      ),
      value: ""
    }
  };

  let propertiesToReturn = prop(asetuksenKohdeavain, properties);

  if (
    asetuksenKohdeavain === "kokonaismaara" ||
    asetuksenKohdeavain === "opiskelijamaarat"
  ) {
    const kujalisamaareet = await localForage.getItem(
      PaikallisenTietovarastonAvain.KUJALISAMAAREET
    );
    propertiesToReturn = {
      closeMenuOnSelect: true,
      inputId,
      isMulti: false,
      isReadOnly,
      isVisible: !isReadOnly,
      options:
        index === 0
          ? map(maare => {
              const koodistoUri = path(["koodisto", "koodistoUri"], maare);
              return {
                value: `${koodistoUri}_${maare.koodiarvo}`,
                label: maare.metadata[localeUpper].nimi
              };
            }, kujalisamaareet)
          : getOptionObjects(
              [
                koulutustyyppi === "1" ? "opetustehtavat" : null,
                "toimintaalue",
                koulutustyyppi === "4" ? "muutKunnatJoistaOppilaita" : null,
                "opetuskielet",
                koulutustyyppi === "1" ? "opetuksenJarjestamismuodot" : null,
                "erityisetKoulutustehtavat",
                maaraaikaOption,
                koulutustyyppi === "1" || koulutustyyppi === "2"
                  ? "oppilaitokset"
                  : null
              ].filter(Boolean)
            )
    };
  }

  return propertiesToReturn
    ? {
        anchor: "kohde",
        name: "Autocomplete",
        layout: { indentation: "none" },
        styleClasses: ["w-4/5 xl:w-2/3 mb-6"],
        properties: propertiesToReturn
      }
    : {
        anchor: "ei-valittavia-kohteita",
        name: "StatusTextRow",
        properties: {
          title: "Ei valittavia kohteita"
        }
      };
};
